import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { z } from 'zod';
import { loginRequest, type LoginRequest } from '@brandschutz/shared';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { PoweredByBIK } from '@/components/brand/PoweredByBIK';
import { useAuth } from '@/lib/auth-context';
import { usePublicBranding } from '@/lib/branding';
import { Icon } from '@/lib/icons';
import { ApiError } from '@/lib/api';
import { duration, easing } from '@/lib/motion';
import { SplashScreen, hasSeenSplash } from '@/components/brand/SplashScreen';

// SECURITY: keine Klartext-Zugangsdaten im Code. Die Quick-Login-Liste kommt
// optional aus VITE_DEV_LOGINS_JSON (z. B. in apps/web/.env.local, gitignored)
// und wird nur im Dev-Build ausgewertet. Passwörter stammen aus dem
// Seed-Output bzw. den SEED_*-Variablen (siehe apps/api/prisma/seed.ts).
const demoUserSchema = z.array(
  z.object({ role: z.string(), email: z.string(), password: z.string() }),
);

function parseDemoUsers(raw: unknown): Array<{ role: string; email: string; password: string }> {
  if (!import.meta.env.DEV) return [];
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  try {
    return demoUserSchema.parse(JSON.parse(raw));
  } catch {
    return [];
  }
}

const DEMO_USERS = parseDemoUsers(import.meta.env.VITE_DEV_LOGINS_JSON);

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const branding = usePublicBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const [serverError, setServerError] = useState<string | null>(null);
  const [showDemoUsers, setShowDemoUsers] = useState(import.meta.env.DEV);
  const [showPassword, setShowPassword] = useState(false);
  // 2FA-Schritt: wird sichtbar, sobald das API mit `two-factor-required`
  // antwortet — E-Mail/Passwort waren dann bereits korrekt.
  const [otpRequired, setOtpRequired] = useState(false);
  // Splash erscheint pro Browser-Session genau einmal vor dem Login.
  const [splashDone, setSplashDone] = useState(() => hasSeenSplash());

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginRequest),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated) return <Navigate to={from} replace />;
  if (!splashDone) return <SplashScreen onDone={() => setSplashDone(true)} />;

  const onSubmit = async (values: LoginRequest) => {
    setServerError(null);
    try {
      await login(values);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const problemType = err.problem.type ?? '';
        if (problemType.endsWith('/two-factor-required')) {
          // Zugangsdaten stimmen — jetzt den zweiten Faktor nachfragen.
          setOtpRequired(true);
          return;
        }
        if (problemType.endsWith('/two-factor-invalid')) {
          setServerError('Der Zwei-Faktor-Code ist ungültig oder abgelaufen.');
          return;
        }
        setServerError(
          err.problem.status === 401
            ? 'E-Mail oder Passwort sind nicht korrekt.'
            : err.problem.title,
        );
      } else if (err instanceof Error) {
        setServerError(err.message);
      } else {
        setServerError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
      }
    }
  };

  const quickLogin = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    // theme-fixed-light: der Login-Screen ist ein fester Marken-Auftritt
    // (dunkler Hero, helle Karte) und soll im Dunkelmodus nicht umklappen.
    <div className="theme-fixed-light relative flex min-h-screen items-center justify-center bg-ink-950 px-4 py-8">
      {/* Logo-Icon blurred als Hero-Background (Ignis AI Marke).
          PERF: bewusst die kleine mark-256 (33 KB) statt icon-dark (815 KB) —
          durch den starken Blur ist der Qualitätsunterschied unsichtbar,
          spart aber ~800 KB auf dem allerersten Screen. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-contain bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/brand/ignisai-mark-256.png)',
          filter: 'blur(22px) brightness(0.7)',
          transform: 'scale(1.15)',
        }}
      />
      {/* Dunkles Overlay für Lesbarkeit + Vignette */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-ink-950/55" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgb(47 118 181 / 0.16), transparent 60%)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.slow, ease: easing.smooth }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Brand block */}
        <div className="mb-8 flex flex-col items-center text-center">
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: duration.slow, ease: easing.soft }}
          >
            <img
              src="/brand/ignisai-mark-256.png"
              alt="Ignis AI"
              width={112}
              height={112}
              className="h-[112px] w-[112px] object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
            />
          </motion.div>
          <h1 className="mt-5 text-3xl font-bold tracking-tight text-paper drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
            Ignis<span className="text-accent-400"> AI</span>
          </h1>
          <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.25em] text-ink-300">
            Brandschutzmanagement
          </p>
          <p className="mt-4 text-sm text-ink-200">Brandschutz. Intelligent geplant.</p>
        </div>

        {/* Card */}
        <div className="rounded-lg border border-ink-200 bg-paper p-7 shadow-elev-3">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <Input
              label="E-Mail"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="name@ihrbuero.de"
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Passwort"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              error={errors.password?.message}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="-mr-1 rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-800"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={14} />
                </button>
              }
              {...register('password')}
            />

            {otpRequired ? (
              <>
                <div className="flex items-start gap-2 rounded border border-accent-300 bg-accent-50 px-3 py-2 text-sm text-accent-900">
                  <Icon name="ShieldCheck" size={14} className="mt-0.5" />
                  <span>
                    Zwei-Faktor-Authentifizierung aktiv — bitte den Code aus Ihrer Authenticator-App
                    eingeben.
                  </span>
                </div>
                <Input
                  label="Zwei-Faktor-Code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                  placeholder="123456"
                  hint="6-stelliger Code oder ein Recovery-Code (XXXX-XXXX)"
                  error={errors.otp?.message}
                  {...register('otp', {
                    setValueAs: (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined),
                  })}
                />
              </>
            ) : null}

            {serverError ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger"
              >
                <Icon name="AlertCircle" size={14} className="mt-0.5" />
                <span>{serverError}</span>
              </div>
            ) : null}

            <Button type="submit" loading={isSubmitting} fullWidth size="lg">
              Anmelden
            </Button>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-ink-500 hover:text-ink-800">
                Passwort vergessen?
              </Link>
            </div>
          </form>
        </div>

        {/* Demo-User-Disclosure (nur Dev + nur wenn VITE_DEV_LOGINS_JSON gesetzt) */}
        {import.meta.env.DEV && DEMO_USERS.length > 0 ? (
          <details
            open={showDemoUsers}
            onToggle={(e) => setShowDemoUsers((e.target as HTMLDetailsElement).open)}
            className="mt-4 rounded-lg border border-ink-200 bg-paper/60 px-4 py-3 text-xs text-ink-600"
          >
            <summary className="cursor-pointer select-none font-medium text-ink-700">
              Demo-Nutzer (nur Entwicklung)
            </summary>
            <p className="mt-2 text-[10px] text-ink-500">
              Klick auf eine Zeile übernimmt die Zugangsdaten — nur in der lokalen
              Entwicklungsumgebung sichtbar.
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-1.5">
              {DEMO_USERS.map((u) => (
                <li key={u.email}>
                  <button
                    type="button"
                    onClick={() => quickLogin(u.email, u.password)}
                    className="flex w-full flex-wrap items-center justify-between gap-2 rounded border border-ink-200 bg-paper px-3 py-2 text-left transition-colors hover:border-ink-300 hover:bg-ink-50"
                  >
                    <span className="font-medium text-ink-800">{u.role}</span>
                    <div className="flex flex-col items-end leading-tight">
                      <span className="font-mono text-[11px] text-ink-700">{u.email}</span>
                      <span className="font-mono text-[11px] text-ink-500">{u.password}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {/* Footer */}
        <div className="mt-10 flex flex-col items-center gap-1 text-center">
          <p className="text-xs text-ink-300">
            © {branding.officeName} {new Date().getFullYear()} · {branding.footerText}
          </p>
          <PoweredByBIK variant="dark" />
        </div>
      </motion.div>
    </div>
  );
}
